import { Mail, Phone, MapPin, MessageCircle, Facebook } from "lucide-react";
import { Reveal, SectionHeader } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { SITE, waLink, messengerLink } from "@/config/site";
import { Seo } from "@/components/Seo";
import { crumbs, contactJsonLd } from "@/config/seo";

export default function Contact() {
  const channels = [
    { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-channel-email" },
    { icon: MessageCircle, label: "WhatsApp", value: SITE.phoneDisplay, href: waLink("Hi MetalMuse! I'd like to ask about a metal print."), testid: "contact-channel-whatsapp" },
    { icon: Facebook, label: "Messenger", value: "Message us on Facebook", href: messengerLink, testid: "contact-channel-messenger" },
    { icon: MapPin, label: "Location", value: SITE.region, href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(SITE.mapsQuery)}`, testid: "contact-channel-location" },
  ];

  return (
    <div data-testid="contact-page" className="pt-32">
      <Seo
        title="Contact MetalMuse — Regina, Saskatchewan Metal Prints"
        description="Talk to a real human at MetalMuse. Reach us by email, WhatsApp, Messenger or the contact form. Based in Regina, serving all of Saskatchewan with same-day delivery and pickup."
        path="/contact"
        keywords="contact MetalMuse, Regina metal prints contact, Saskatchewan photo prints, metal print quote"
        jsonLd={[crumbs([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]), contactJsonLd()]}
      />
      <section className="container-lux pb-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div>
          <SectionHeader eyebrow="Contact" title="Talk to a real human" text="Every order and question is handled personally. Reach us on any channel — or send the form and we'll come to you." />
          <div className="flex flex-col gap-4">
            {channels.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.06}>
                <a href={c.href} target={c.href.startsWith("mailto") ? undefined : "_blank"} rel="noopener noreferrer" data-testid={c.testid} className="flex items-center gap-5 bg-[#121212] border border-white/5 rounded-2xl px-6 py-5 hover:border-[#00E5FF]/30 transition-colors duration-500 group">
                  <div className="w-11 h-11 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center shrink-0">
                    <c.icon size={20} className="text-[#00E5FF]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#A1A1A6]">{c.label}</p>
                    <p className="text-[#F5F5F7] font-medium mt-1 group-hover:text-[#00E5FF] transition-colors duration-300">{c.value}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15}>
          <ContactForm />
        </Reveal>
      </section>

      <section className="bg-[#0A0A0A] border-t border-white/5 section-spacing">
        <div className="container-lux">
          <SectionHeader eyebrow="Find Us" title="Proudly based in Saskatchewan" text="Serving Saskatoon, Regina and communities across the province. Pickup available for qualifying orders." />
          <Reveal>
            <div data-testid="google-map-placeholder" className="rounded-2xl overflow-hidden border border-white/10 relative">
              <iframe
                title="MetalMuse service area map — Saskatchewan, Canada"
                src={`https://www.google.com/maps?q=${encodeURIComponent(SITE.mapsQuery)}&output=embed`}
                className="w-full h-[420px] grayscale-[60%] contrast-[1.05]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute top-4 left-4 glass rounded-full px-5 py-2.5 flex items-center gap-2">
                <Phone size={14} className="text-[#00E5FF]" />
                <span className="text-xs font-medium text-[#F5F5F7]">Same-day delivery & pickup available</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
